import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { CashOccurrenceType } from '../../../app/enums';
import {
    useLiquidityRecurringCashflowSettings,
    useSelectAllLiquidityRecurringCashflowItems,
    useUpdateLiquidityRecurringCashflowItemSettings,
    useUpdateLiquidityRecurringCashflowPanelSettings,
} from '../../api';
import { useLiquidityCalculationData, useLiquidityScopeUpdating } from '../../hooks';
import { LiquidityCashflowSettingBox } from './liquidity-cashflow-setting-box';

interface IProps {
    expandedKey?: string;
}

export const LiquidityRecurringCashflowSettings: FunctionComponent<IProps> = ({ expandedKey }) => {
    const { t } = useTranslation();
    const calculationData = useLiquidityCalculationData();
    const { data: cashFlowSettings, isFetching: isFetchingCashFlows } = useLiquidityRecurringCashflowSettings();
    const isScopeUpdating = useLiquidityScopeUpdating();
    // The liquidity recurring panel always consumes its own endpoint; the backend decides whether the
    // items come from PCM cash-flows or from the income/expense surplus (expanded into per-year buckets).
    const cashFlows = cashFlowSettings?.itemsSettings;

    const updateCashFlowSettings = useUpdateLiquidityRecurringCashflowPanelSettings();
    const updateCashFlowItem = useUpdateLiquidityRecurringCashflowItemSettings();
    const selectAllCashFlows = useSelectAllLiquidityRecurringCashflowItems();

    if (calculationData.isLoading) return null;

    return (
        <LiquidityCashflowSettingBox
            title={t('recurringCashflows')}
            cashOccurrenceType={CashOccurrenceType.RECURRING}
            settings={cashFlowSettings}
            cashflowItems={cashFlows ?? []}
            updatePanelSettings={updateCashFlowSettings}
            updateItemSettings={updateCashFlowItem}
            selectAllItems={selectAllCashFlows}
            sectionKey="cashflows"
            expandedKey={expandedKey}
            isUpdating={isFetchingCashFlows || isScopeUpdating}
        />
    );
};
